import { useEffect, useState } from "react";
import { Mail, Phone, ExternalLink, Trash2, Loader2, Inbox } from "lucide-react";
import { toast } from "sonner";
import { api } from "./api";

const STATUS_COLORS = {
  new: "bg-brand text-white",
  reviewed: "bg-zinc-700 text-zinc-100",
  archived: "bg-zinc-900 text-zinc-500",
};

export const Inquiries = () => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const load = async () => {
    try {
      setLoading(true);
      const { data } = await api.get("/cms/inquiries");
      setList(data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not load inquiries");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/cms/inquiries/${id}`, { status });
      setList((cur) => cur.map((i) => (i.id === id ? { ...i, status } : i)));
      if (selected?.id === id) setSelected((s) => ({ ...s, status }));
      toast.success(`Marked ${status}`);
    } catch {
      toast.error("Could not update");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this inquiry?")) return;
    try {
      await api.delete(`/cms/inquiries/${id}`);
      setList((cur) => cur.filter((i) => i.id !== id));
      if (selected?.id === id) setSelected(null);
      toast.success("Deleted");
    } catch {
      toast.error("Could not delete");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-brand-light font-bold">CMS</div>
          <h1 className="font-display uppercase text-3xl md:text-4xl mt-1">Program Inquiries</h1>
          <p className="text-sm text-zinc-400 mt-2">
            Submissions from the inquiry forms. Resend email delivery is wired in once the domain is verified.
          </p>
        </div>
        <button onClick={load} className="btn-ghost !py-2.5 !px-4 !text-xs" data-testid="inquiries-refresh">
          {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null} Refresh
        </button>
      </div>

      {loading ? (
        <div className="py-20 text-center text-zinc-500"><Loader2 className="h-5 w-5 animate-spin inline-block" /></div>
      ) : list.length === 0 ? (
        <div className="bento-card p-10 text-center" data-testid="inquiries-empty">
          <Inbox className="h-8 w-8 text-zinc-500 mx-auto mb-4" />
          <div className="text-zinc-400">No inquiries yet.</div>
          <div className="text-xs text-zinc-500 mt-2">Submissions from On-Ice Training and BDH Hockey Development CTAs will show up here.</div>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.2fr] gap-5">
          <div className="space-y-2" data-testid="inquiries-list">
            {list.map((i) => (
              <button
                key={i.id}
                onClick={() => setSelected(i)}
                className={`w-full text-left bento-card p-4 transition-colors ${selected?.id === i.id ? "border-brand-light" : "hover:border-white/20"}`}
                data-testid={`inquiry-row-${i.id}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-semibold truncate">{i.name}</div>
                    <div className="text-xs text-zinc-400 truncate">{i.email}{i.phone ? ` · ${i.phone}` : ""}</div>
                    <div className="text-[10px] tracking-[0.25em] uppercase text-brand-light font-bold mt-2 truncate">{i.program || "—"}</div>
                  </div>
                  <span className={`text-[9px] tracking-[0.2em] uppercase font-bold px-2 py-1 ${STATUS_COLORS[i.status] || STATUS_COLORS.new}`}>
                    {i.status || "new"}
                  </span>
                </div>
                <div className="text-[10px] text-zinc-500 mt-3">
                  {new Date(i.created_at).toLocaleString()}
                </div>
              </button>
            ))}
          </div>

          {selected ? (
            <div className="bento-card p-6 sticky top-4 self-start" data-testid="inquiry-detail">
              <div className="flex items-start justify-between gap-4 mb-5">
                <div>
                  <div className="text-[10px] tracking-[0.3em] uppercase text-brand-light font-bold">{selected.program || "Inquiry"}</div>
                  <div className="font-display text-2xl uppercase mt-1">{selected.name}</div>
                </div>
                <span className={`text-[9px] tracking-[0.2em] uppercase font-bold px-2 py-1 ${STATUS_COLORS[selected.status] || STATUS_COLORS.new}`}>
                  {selected.status || "new"}
                </span>
              </div>

              <div className="space-y-3 text-sm">
                <a href={`mailto:${selected.email}`} className="flex items-center gap-2 text-zinc-200 hover:text-brand-light">
                  <Mail className="h-4 w-4" /> {selected.email}
                </a>
                {selected.phone && (
                  <a href={`tel:${selected.phone}`} className="flex items-center gap-2 text-zinc-200 hover:text-brand-light">
                    <Phone className="h-4 w-4" /> {selected.phone}
                  </a>
                )}
                {selected.source_path && (
                  <div className="flex items-center gap-2 text-zinc-500 text-xs">
                    <ExternalLink className="h-3.5 w-3.5" /> Submitted from <span className="text-zinc-300">{selected.source_path}</span>
                  </div>
                )}
              </div>

              {selected.message && (
                <div className="mt-5">
                  <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-brand-light mb-2">Message</div>
                  <p className="text-zinc-200 whitespace-pre-wrap text-sm leading-relaxed">{selected.message}</p>
                </div>
              )}

              <div className="mt-6 flex flex-wrap gap-2">
                {selected.status !== "reviewed" && (
                  <button onClick={() => setStatus(selected.id, "reviewed")} className="btn-ghost !py-2 !px-3 !text-xs">Mark reviewed</button>
                )}
                {selected.status !== "new" && (
                  <button onClick={() => setStatus(selected.id, "new")} className="btn-ghost !py-2 !px-3 !text-xs">Mark new</button>
                )}
                {selected.status !== "archived" && (
                  <button onClick={() => setStatus(selected.id, "archived")} className="btn-ghost !py-2 !px-3 !text-xs">Archive</button>
                )}
                <button onClick={() => remove(selected.id)} className="btn-ghost !py-2 !px-3 !text-xs border-red-500/40 text-red-300 hover:border-red-500">
                  <Trash2 className="h-3.5 w-3.5" /> Delete
                </button>
              </div>
            </div>
          ) : (
            <div className="bento-card p-10 text-center text-zinc-500 lg:sticky lg:top-4 lg:self-start">
              Select an inquiry to view details.
            </div>
          )}
        </div>
      )}
    </div>
  );
};
